import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfigService } from '@app/config/config.service';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import * as FullStory from '@fullstory/browser';

/**
 * Interceptor that identifies / aggregates users in FullStory for specific HTTP requests.
 * See: https://developer.fullstory.com/browser/v1/identification/identify-users/
 * TODO: We might consider a global http interceptor that interacts with a series of services, rather than 3+ separate http interceptors
 */
@Injectable()
export class FullstoryInterceptor implements HttpInterceptor {
  constructor(private readonly appConfigService: AppConfigService) {}

  /**
   * Flag indicating whether Fullstory is deactivated
   * Lazily initialized from the app configuration.
   */
  public fsDeactivated = null;

  /**
   * Flag indicating whether to identify users in FullStory.
   * Lazily initialized from the app configuration.
   */
  public fsIdUsers = null;

  /**
   * The length of the substring to keep when masking sensitive information.
   */
  public maskLegnth = 5;

  /**
   * Intercepts HTTP requests and identifies users in FullStory for specific requests.
   * @param request The HTTP request object.
   * @param next The HTTP handler to forward the request to.
   * @returns An observable of the HTTP event.
   */
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Identify users in FullStory only for POST requests to 'account/register' endpoint
    // NOTE: When the WW is migrated into the DX Repo, we can likely do this at the page, card, or even field level
    if (request.method === 'POST' && request.url.includes('account/register') && this.identifyUsers) {
      return next.handle(request).pipe(
        tap({
          next: (event) => {
            if (event instanceof HttpResponse) {
              const data = event?.body?.data;

              // Mask the first name, last name, email, and cell phone
              const firstName = this.maskString(data?.firstName);
              const lastName = this.maskString(data?.lastName);
              const email = this.maskEmail(data?.email);
              const cellPhone = this.maskString(data?.cellPhone);

              FullStory.identify(email, {
                name: `${firstName} ${lastName}`,
                isCellPhoneUser: data?.isCellPhoneUser,
                email,
                cellPhone
              });
            }
          }
        })
      );
    }
    return next.handle(request);
  }

  /**
   * Lazy gets the value of the identifyUsers flag from the app configuration.
   * @returns The boolean value indicating whether to identify users or not.
   */
  private get identifyUsers(): boolean {
    const stringToBoolean = (s: string): boolean => (!s || s.toLowerCase() === 'null' || s.toLowerCase() === 'false' ? false : true);
    if (this.fsIdUsers === null) this.fsIdUsers = stringToBoolean(this.appConfigService.getData().fullStory.identifyUsers);
    if (this.fsDeactivated === null) this.fsDeactivated = stringToBoolean(this.appConfigService.getData().fullStory.deactivate);

    if (this.fsIdUsers && this.fsDeactivated) console.warn('FullStory is deactivated, but id is enabled. This is likely a mistake.');
    return this.fsIdUsers && !this.fsDeactivated;
  }

  /**
   * Masks a string by replacing characters after a certain length with asterisks.
   * @param s The string to mask.
   * @returns The masked string.
   */
  public maskString(s: string): string {
    if (!s) return null;
    return s.length > this.maskLegnth ? s.substring(0, this.maskLegnth) + '*'.repeat(s.length - this.maskLegnth) : s;
  }

  /**
   * Masks an email address by replacing characters in the username part with asterisks.
   * @param email The email address to mask.
   * @returns The masked email address.
   */
  public maskEmail(email: string): string {
    if (!email || !email.includes('@')) return null;
    // Split the email into username and domain parts
    const [username, domain] = email.split('@', 2);
    const maskedUsername =
      username.length > this.maskLegnth ? username.substring(0, this.maskLegnth) + '*'.repeat(username.length - this.maskLegnth) : username;
    return `${maskedUsername}@${domain}`;
  }
}
